import { computed, ref, getCurrentInstance} from "@vue/composition-api";
import store from "@/store";
import axios from "@/libs/axios";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { useToast } from "vue-toastification/composition";
import { encryptData } from "@/utils/encryptUserDataUtils";
import { debounce } from "lodash";
import { useTranslates } from "@/utils/useTranslates";

export function useAppColors(emit, themeConfig, isEdit) {
  const { t } = useI18nUtils();
  const toast = useToast();
  const vm = getCurrentInstance();
  const proxy = vm.root.proxy;
  const trans = {
    "account_settings.appColors.updating": t("account_settings.appColors.updating"),
    "account_settings.appColors.success": t("account_settings.appColors.success"),
    "account_settings.appColors.success_delete": t("account_settings.appColors.success_delete"),
    "account_settings.appColors.confirm_delete": t("account_settings.appColors.confirm_delete"),
    "account_settings.appColors.success_edit": t("account_settings.appColors.success_edit"),
    "account_settings.appColors.success_create": t("account_settings.appColors.success_create"),
    "actions.delete": t("actions.delete"),
    "actions.cancel": t("actions.cancel"),
  };
  const loadingToast = ref();
  const themeName = ref();
  const themeIdTenant = ref();
  const isDark = ref(false)
  const isDarkComponents = ref(false)
  const loadingSaveTheme = ref(false);
  const preferences = computed(() => store.getters["user/getPreferences"]);
  const user = computed(() => store.getters["user/getUser"]);
  const tenantOptions = computed(() => store.getters["grid/getAllTenants"]);
  if (!tenantOptions.value.length) {
    getTenants()
  }
  //defaults values
  const defaultColors = {
    primary: { name: "primary", value:"#1b3d5e", showPicker: false},
    secondary: { name: "secondary", value:"#971f1e", showPicker: false},
    success: { name: "success", value:"#28c76f", showPicker: false},
    danger: { name: "danger", value:"#ea5455", showPicker: false},
    warning: { name: "warning", value:"#ffa500", showPicker: false},
    info: { name: "info", value:"#3e8cc7", showPicker: false},
    light: { name: "light", value:"#0e1e2d", showPicker: false},
    dark: { name: "dark", value:"#282f46", showPicker: false},
    mainLight:{ name: "main-light", value:"#ffffff", showPicker: false},
    secondLight: { name: "second-light", value:"#708599 ", showPicker: false},
    thirdLight: { name: "third-light", value:"#f2f2f2", showPicker: false},
    mainDark: { name: "main-dark", value:"#385d7b", showPicker: false},
    secondDark: { name: "second-dark", value:"#4a708d", showPicker: false},
    thirdDark: { name: "third-dark", value:"#0e1e2d", showPicker: false},
  };

  const colors = ref(isEdit ? getColorsFromConfig() : defaultColors);
  const cardRefs = ref([])
  const inputColorRefs = ref([])
  const dropzoneOptions = ref({
    url: "https://httpbin.org/post",
    paramName: "image",
    autoProcessQueue: false,
    dictDefaultMessage: "",
    addRemoveLinks: true,
    dictRemoveFile: trans["actions.delete"],
    acceptedFiles: "image/*",
  });
  const perPage = ref(10)
  const currentPage = ref(1)
  const logoImageFile = ref({url:null, uuid: null})
  const imageLoadingFile = ref({url:null, uuid: null})
  const loadingGetThemes = ref(false)
  const search = ref('')
  const perPageThemes = ref(10)
  const currentPageThemes = ref(1)
  const themesTotalList = ref(0)
  const themes = ref([])
  const currentTenantSelected = ref([])
  const defaultTheme = {
    name: 'Sbo default',
    id: 'default',
    config: {
      appColors: defaultColors,
      imageLogo: null,
      imageLogo: null
    }
  }
  const currentThemeUsed = ref(preferences.value.theme ?? defaultTheme)
  const nextThemeToUse = ref(null)

  function togglePicker(key) {
    Object.entries(colors.value).forEach(([colorKey, color]) => {
      color.showPicker = (colorKey === key) ? !color.showPicker : false
      if (color.showPicker) {
        const index = Object.keys(colors.value).indexOf(key)
        const input = inputColorRefs.value[index]?.$el
        if (input) input.click()
      }
    })
  }

  function handleClickOutside(event) {
    let clickedInside = cardRefs.value.some(cardEl => {
      return cardEl && cardEl.contains(event.target)
    })
    if (!clickedInside) {
      Object.entries(colors.value).forEach(card => (card.showPicker = false))
    }
  }

  function createAppColorsConfig(event) {
    return {
      appColors: {...colors.value},
      imageLogo: logoImageFile.value,
      imageLogoLoading: imageLoadingFile.value
    }
  }

  async function saveTheme() {
    try {
      const appColorsConfig = createAppColorsConfig()
      const payload = {
        id_tenant: themeIdTenant.value,
        config: appColorsConfig,
        name: themeName.value
      }
      loadingSaveTheme.value = true
      loadingToast.value?.show( trans["account_settings.appColors.updating"], trans["account_settings.appColors.updating"],"secondary");
      const response = !isEdit ? await axios.post("v1/themes", payload) : await axios.put(`v1/themes/${themeConfig.id}`, payload);
      if(isEdit) {
        reloadCurrentTheme(response.data.data)
      }
      loadingSaveTheme.value = false
      emit('closeThemeBuilder', true)
      loadingToast.value?.close();
      const messageToast = isEdit ? trans["account_settings.appColors.success_edit"] : trans["account_settings.appColors.success_create"]
      contentToast(messageToast, "success")
    } catch(e) {
      console.log(e)
      loadingToast.value?.close();
      contentToast("Error on change colors", "danger")
      loadingSaveTheme.value = false
    }
  }

  function saveUserConfigToLocalStorage(config) {
    const userData = JSON.parse(localStorage.getItem("userData"));
    userData.config = config;
    const encryptetUserData = encryptData(userData);
    localStorage.setItem("userData", encryptetUserData);
  }

  function contentToast (message, type) {
    toast({
      component: ToastificationContent,
      props: {
        title: message,
        icon: "CheckIcon",
        variant: type,
      },
    });
  }

  function getColorsFromConfig() {
    return themeConfig.config.appColors;
  }

  function toggleDarkMode() {
    isDark.value = !isDark.value
  }
  function toggleDarkModeComponents() {
    isDarkComponents.value = !isDarkComponents.value
  }

  function getColorOfLayoutMode(color) {
    const type = isDark.value ? 'Dark' : 'Light'
    const coloname = color + type
    return colors.value[coloname].value;
  }
  function readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function addLogoImage(file) {
    readFileAsDataURL(file).then((dataURL) => {
      logoImageFile.value.url = dataURL;
    });
  }

  function removeLogoImage() {
    logoImageFile.value.url = null;
  }

  function addLogoLoading(file) {
    readFileAsDataURL(file).then((dataURL) => {
      imageLoadingFile.value.url = dataURL;
    });
  }

  function removeLogoLoading() {
    imageLoadingFile.value.url = null;
  }

  async function getThemes() {
    try {
      const ids = currentTenantSelected.value.length ? currentTenantSelected.value : tenantOptions.value.map(item => item.id);
      const payload = {
        id_tenant: ids,
        q: search.value,
        max: perPageThemes.value,
        "page[n]": currentPageThemes.value,
      }
      loadingGetThemes.value = true
      themes.value = []
      const {data} = await axios.get("v1/themes", {params: payload});
      themes.value = data.data.data
      themesTotalList.value = data.data.total;
      loadingGetThemes.value = false
    } catch(e) {
      console.log(e)
      contentToast("Error on getting themes", "danger")
      loadingGetThemes.value = false
    }
  }

  function updateCurrentPage(page) {
    currentPageThemes.value = page
    getThemes()
  }

  const onTypeSearch = debounce(function (event) { getThemes()}, 800);

  function setEditValues(theme) {
    themeName.value = theme.name;
    themeIdTenant.value = theme.id_tenant;
    if(theme.config.imageLogo?.url) logoImageFile.value = theme.config.imageLogo
    if(theme.config.imageLogoLoading?.url) imageLoadingFile.value = themeConfig.config.imageLogoLoading
  }

  async function deleteTheme(theme) {
    try {
      const confirmation = await confirmDeleteTheme(theme)
      if (!confirmation.isConfirmed) return;
      await axios.delete(`v1/themes/${theme.id}`)
      getThemes()
      contentToast(trans["account_settings.appColors.success_delete"], "success")
    } catch(e) {
      console.log(e)
      contentToast("Error on delete theme", "danger")
    }
  }

  const confirmDeleteTheme = async (theme) => {
    const $swal = proxy?.$swal;

    return await $swal({
      title: useTranslates(trans["account_settings.appColors.confirm_delete"], { theme: theme.name }),
      icon: "warning",
      showCancelButton: true,
      cancelButtonText: trans["actions.cancel"],
      confirmButtonText: trans["actions.delete"],
      customClass: {
        confirmButton: "btn btn-primary text-capitalize",
        cancelButton: "btn btn-outline-danger ml-1 text-capitalize",
      },
      buttonsStyling: false,
    });
  };

  function selectNextTheme(theme) {
    nextThemeToUse.value = theme
  }

  async function changeTheme() {
    try {
      const config = {...user.value.config}
      config.preferences.theme = nextThemeToUse.value
      loadingToast.value.show( trans["account_settings.appColors.updating"], trans["account_settings.appColors.updating"],"secondary");
      await axios.put("v1/users/update-config", { config });
      store.commit("user/updateUserConfig", config);
      saveUserConfigToLocalStorage(config)
      loadingToast.value.close();
      contentToast(trans["account_settings.appColors.success"], "success")
      setTimeout(() => {
        window.location.reload()
      }, 1200);
    } catch(e) {
      console.log(e)
      loadingToast.value.close();
      contentToast("Error on change colors", "danger")
    }
  }

  function reloadCurrentTheme(theme) {
    if(currentThemeUsed.value.id !== theme.id) return;
    nextThemeToUse.value = theme
    changeTheme()
  }

  async function getTenants() {
    const {data} = await axios.get('/v1/services/tenants/tenants-by-user')
    store.commit("grid/setTenants", data.data);
    return data?.data
  }

  return {
    colors,
    cardRefs,
    inputColorRefs,
    loadingToast,
    loadingSaveTheme,
    isDark,
    trans,
    dropzoneOptions,
    perPage,
    currentPage,
    logoImageFile,
    imageLoadingFile,
    themeName,
    themeIdTenant,
    tenantOptions,
    perPageThemes,
    currentPageThemes,
    search,
    themes,
    themesTotalList,
    currentTenantSelected,
    loadingGetThemes,
    nextThemeToUse,
    currentThemeUsed,
    defaultTheme,
    isDarkComponents,
    togglePicker,
    handleClickOutside,
    saveTheme,
    toggleDarkMode,
    getColorOfLayoutMode,
    addLogoImage,
    removeLogoImage,
    addLogoLoading,
    removeLogoLoading,
    getThemes,
    updateCurrentPage,
    onTypeSearch,
    setEditValues,
    deleteTheme,
    selectNextTheme,
    changeTheme,
    toggleDarkModeComponents
  }
}